import React, { useState, useEffect } from 'react';
import { FileText, BarChart3, TrendingUp, Award, Calendar, Filter, Loader, Eye } from 'lucide-react';
import Layout from '../Layout';
import ParentAPI from '../services/ParentAPI';
import { showErrorNotification } from '../utils/errorHandler';

const AssessmentResults = () => {
    const [selectedChild, setSelectedChild] = useState(null);
    const [children, setChildren] = useState([]);
    const [assessmentResults, setAssessmentResults] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filterType, setFilterType] = useState('all');
    const [filterSubject, setFilterSubject] = useState('all');

    useEffect(() => {
        loadChildren();
    }, []);

    useEffect(() => {
        if (selectedChild) {
            loadAssessmentResults();
        }
    }, [selectedChild]);

    const loadChildren = async () => {
        try {
            const childrenData = await ParentAPI.getChildren();
            setChildren(childrenData);
            
            if (childrenData.length > 0) {
                setSelectedChild(childrenData[0]);
            }
        } catch (err) {
            console.error('Error loading children:', err);
            showErrorNotification('Failed to load children data');
        } finally {
            setLoading(false);
        }
    };

    const loadAssessmentResults = async () => {
        if (!selectedChild) return;

        try {
            setLoading(true);
            
            // Mock assessment results data
            const mockResults = [
                {
                    id: '1',
                    title: 'Mathematics Quiz - Numbers & Counting',
                    type: 'Quiz',
                    subject: 'Mathematics',
                    date: '2024-01-15',
                    score: 94,
                    maxScore: 100,
                    grade: 'A',
                    duration: '30 minutes',
                    competencies: ['Number Recognition', 'Counting Skills', 'Basic Addition'],
                    feedback: 'Excellent understanding of number concepts. Shows strong counting abilities.',
                    teacher: 'Ms. Johnson'
                },
                {
                    id: '2',
                    title: 'Language Arts - Story Comprehension',
                    type: 'Assessment',
                    subject: 'Language Arts',
                    date: '2024-01-12',
                    score: 88,
                    maxScore: 100,
                    grade: 'B+',
                    duration: '45 minutes',
                    competencies: ['Reading Comprehension', 'Vocabulary', 'Critical Thinking'],
                    feedback: 'Good comprehension skills. Needs to work on vocabulary expansion.',
                    teacher: 'Ms. Smith'
                },
                {
                    id: '3',
                    title: 'Science Observation - Plant Life Cycle',
                    type: 'Project',
                    subject: 'Science',
                    date: '2024-01-10',
                    score: 96,
                    maxScore: 100,
                    grade: 'A',
                    duration: '1 week',
                    competencies: ['Scientific Observation', 'Data Recording', 'Analysis'],
                    feedback: 'Outstanding observation skills and detailed documentation.',
                    teacher: 'Mr. Brown'
                },
                {
                    id: '4',
                    title: 'Creative Arts - Self Portrait',
                    type: 'Portfolio',
                    subject: 'Arts',
                    date: '2024-01-08',
                    score: 92,
                    maxScore: 100,
                    grade: 'A-',
                    duration: '2 hours',
                    competencies: ['Creative Expression', 'Fine Motor Skills', 'Self-Awareness'],
                    feedback: 'Creative and expressive artwork. Shows good attention to detail.',
                    teacher: 'Ms. Davis'
                },
                {
                    id: '5',
                    title: 'Physical Education - Coordination Skills',
                    type: 'Performance',
                    subject: 'Physical Education',
                    date: '2024-01-05',
                    score: 90,
                    maxScore: 100,
                    grade: 'A-',
                    duration: '50 minutes',
                    competencies: ['Gross Motor Skills', 'Coordination', 'Following Instructions'],
                    feedback: 'Excellent coordination and enthusiasm. Great team player.',
                    teacher: 'Coach Wilson'
                }
            ];

            setAssessmentResults(mockResults);
        } catch (err) {
            console.error('Error loading assessment results:', err);
            showErrorNotification('Failed to load assessment results');
        } finally {
            setLoading(false);
        }
    };

    const handleChildSwitch = (childId) => {
        const child = children.find(c => c.student_id === childId);
        setSelectedChild(child);
    };

    const getGradeColor = (grade) => {
        if (grade.startsWith('A')) return 'text-green-600 bg-green-100';
        if (grade.startsWith('B')) return 'text-blue-600 bg-blue-100';
        if (grade.startsWith('C')) return 'text-yellow-600 bg-yellow-100';
        return 'text-gray-600 bg-gray-100';
    };

    const getTypeColor = (type) => {
        const colors = {
            'Quiz': 'bg-blue-500',
            'Assessment': 'bg-green-500',
            'Project': 'bg-purple-500',
            'Portfolio': 'bg-pink-500',
            'Performance': 'bg-orange-500'
        };
        return colors[type] || 'bg-gray-500';
    };

    const filteredResults = assessmentResults.filter(result => {
        const matchesType = filterType === 'all' || result.type.toLowerCase() === filterType.toLowerCase();
        const matchesSubject = filterSubject === 'all' || result.subject === filterSubject;
        return matchesType && matchesSubject;
    });

    const subjects = [...new Set(assessmentResults.map(result => result.subject))];
    const types = [...new Set(assessmentResults.map(result => result.type))];

    const calculateAverageScore = () => {
        if (filteredResults.length === 0) return 0;
        const total = filteredResults.reduce((sum, result) => sum + result.score, 0);
        return Math.round(total / filteredResults.length);
    };

    if (loading && !selectedChild) {
        return (
            <Layout pageTitle="Assessment Results">
                <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50 flex items-center justify-center">
                    <div className="text-center">
                        <Loader className="w-12 h-12 animate-spin mx-auto mb-4 text-indigo-600" />
                        <h2 className="text-2xl font-bold text-gray-800 mb-2">Loading Assessment Results</h2>
                        <p className="text-gray-600">Please wait while we fetch assessment data...</p>
                    </div>
                </div>
            </Layout>
        );
    }

    if (!selectedChild) {
        return (
            <Layout pageTitle="Assessment Results">
                <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50 flex items-center justify-center">
                    <div className="text-center">
                        <FileText className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                        <h2 className="text-2xl font-bold text-gray-800 mb-2">No Child Selected</h2>
                        <p className="text-gray-600">Please select a child to view their assessment results.</p>
                    </div>
                </div>
            </Layout>
        );
    }

    return (
        <Layout pageTitle="Assessment Results">
            <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50 p-6">
                <div className="max-w-7xl mx-auto">
                    {/* Header */}
                    <div className="bg-white rounded-2xl shadow-xl p-6 mb-8">
                        <div className="flex items-center justify-between mb-6">
                            <div>
                                <h1 className="text-3xl font-bold text-gray-800">Assessment Results</h1>
                                <p className="text-gray-600">View {selectedChild?.name}'s assessment performance and progress</p>
                            </div>
                            
                            {/* Child Selector */}
                            {children.length > 1 && (
                                <div className="relative">
                                    <select
                                        value={selectedChild?.student_id || ''}
                                        onChange={(e) => handleChildSwitch(e.target.value)}
                                        className="bg-indigo-50 border border-indigo-200 text-indigo-800 px-4 py-2 rounded-lg font-medium appearance-none cursor-pointer pr-10"
                                    >
                                        {children.map(child => (
                                            <option key={child.student_id} value={child.student_id}>
                                                {child.name} - Grade {child.grade || 'N/A'}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            )}
                        </div>

                        {/* Filters */}
                        <div className="flex flex-wrap gap-4 items-center">
                            <div className="relative">
                                <select
                                    value={filterType}
                                    onChange={(e) => setFilterType(e.target.value)}
                                    className="bg-white border border-gray-300 px-4 py-2 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent appearance-none cursor-pointer pr-10"
                                >
                                    <option value="all">All Types</option>
                                    {types.map(type => (
                                        <option key={type} value={type}>{type}</option>
                                    ))}
                                </select>
                                <Filter className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 pointer-events-none" />
                            </div>

                            <div className="relative">
                                <select
                                    value={filterSubject}
                                    onChange={(e) => setFilterSubject(e.target.value)}
                                    className="bg-white border border-gray-300 px-4 py-2 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent appearance-none cursor-pointer pr-10"
                                >
                                    <option value="all">All Subjects</option>
                                    {subjects.map(subject => (
                                        <option key={subject} value={subject}>{subject}</option>
                                    ))}
                                </select>
                                <Filter className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 pointer-events-none" />
                            </div>
                        </div>
                    </div>

                    {/* Summary Stats */}
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
                        <div className="bg-white rounded-2xl shadow-xl p-6">
                            <div className="flex items-center">
                                <div className="bg-indigo-500 p-3 rounded-xl mr-4">
                                    <FileText className="w-6 h-6 text-white" />
                                </div>
                                <div>
                                    <div className="text-2xl font-bold text-indigo-600">{filteredResults.length}</div>
                                    <div className="text-sm text-gray-600">Total Assessments</div>
                                </div>
                            </div>
                        </div>

                        <div className="bg-white rounded-2xl shadow-xl p-6">
                            <div className="flex items-center">
                                <div className="bg-green-500 p-3 rounded-xl mr-4">
                                    <BarChart3 className="w-6 h-6 text-white" />
                                </div>
                                <div>
                                    <div className="text-2xl font-bold text-green-600">{calculateAverageScore()}%</div>
                                    <div className="text-sm text-gray-600">Average Score</div>
                                </div>
                            </div>
                        </div>

                        <div className="bg-white rounded-2xl shadow-xl p-6">
                            <div className="flex items-center">
                                <div className="bg-purple-500 p-3 rounded-xl mr-4">
                                    <Award className="w-6 h-6 text-white" />
                                </div>
                                <div>
                                    <div className="text-2xl font-bold text-purple-600">
                                        {filteredResults.filter(r => r.score >= 90).length}
                                    </div>
                                    <div className="text-sm text-gray-600">A Grades</div>
                                </div>
                            </div>
                        </div>

                        <div className="bg-white rounded-2xl shadow-xl p-6">
                            <div className="flex items-center">
                                <div className="bg-orange-500 p-3 rounded-xl mr-4">
                                    <TrendingUp className="w-6 h-6 text-white" />
                                </div>
                                <div>
                                    <div className="text-2xl font-bold text-orange-600">
                                        {Math.max(...filteredResults.map(r => r.score), 0)}%
                                    </div>
                                    <div className="text-sm text-gray-600">Highest Score</div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Assessment Results Grid */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        {filteredResults.length > 0 ? (
                            filteredResults.map((result) => (
                                <div key={result.id} className="bg-white rounded-2xl shadow-xl p-6 hover:shadow-2xl transition-all duration-300">
                                    {/* Header */}
                                    <div className="flex items-start justify-between mb-4">
                                        <div className="flex items-center gap-3">
                                            <div className={`w-12 h-12 ${getTypeColor(result.type)} rounded-xl flex items-center justify-center`}>
                                                <FileText className="w-6 h-6 text-white" />
                                            </div>
                                            <div>
                                                <h3 className="text-lg font-bold text-gray-800 line-clamp-2">{result.title}</h3>
                                                <p className="text-sm text-gray-600">{result.subject}</p>
                                            </div>
                                        </div>
                                        <div className="text-right">
                                            <div className={`px-3 py-1 rounded-full text-sm font-bold ${getGradeColor(result.grade)}`}>
                                                {result.grade}
                                            </div>
                                        </div>
                                    </div>

                                    {/* Score */}
                                    <div className="mb-4">
                                        <div className="flex items-center justify-between mb-2">
                                            <span className="text-sm text-gray-600">Score</span>
                                            <span className="text-lg font-bold text-gray-800">
                                                {result.score}/{result.maxScore}
                                            </span>
                                        </div>
                                        <div className="w-full bg-gray-200 rounded-full h-3">
                                            <div 
                                                className="bg-gradient-to-r from-indigo-500 to-purple-600 h-3 rounded-full transition-all duration-500"
                                                style={{ width: `${(result.score / result.maxScore) * 100}%` }}
                                            ></div>
                                        </div>
                                    </div>

                                    {/* Details */}
                                    <div className="space-y-2 mb-4">
                                        <div className="flex items-center gap-2 text-sm text-gray-600">
                                            <Calendar className="w-4 h-4" />
                                            <span>{new Date(result.date).toLocaleDateString()}</span>
                                        </div>
                                        <div className="flex items-center gap-2 text-sm text-gray-600">
                                            <span className={`px-2 py-1 ${getTypeColor(result.type)} text-white text-xs rounded-full`}>
                                                {result.type}
                                            </span>
                                            <span>{result.duration}</span>
                                        </div>
                                    </div>

                                    {/* Competencies */}
                                    <div className="mb-4">
                                        <h4 className="text-sm font-semibold text-gray-700 mb-2">Competencies Assessed:</h4>
                                        <div className="flex flex-wrap gap-2">
                                            {result.competencies.slice(0, 3).map((competency, index) => (
                                                <span 
                                                    key={index}
                                                    className="px-2 py-1 bg-indigo-100 text-indigo-800 text-xs rounded-full"
                                                >
                                                    {competency}
                                                </span>
                                            ))}
                                            {result.competencies.length > 3 && (
                                                <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-full">
                                                    +{result.competencies.length - 3} more
                                                </span>
                                            )}
                                        </div>
                                    </div>

                                    {/* Feedback */}
                                    <div className="bg-gray-50 rounded-lg p-3 mb-4">
                                        <h4 className="text-sm font-semibold text-gray-700 mb-1">Teacher Feedback:</h4>
                                        <p className="text-sm text-gray-600 line-clamp-2">{result.feedback}</p>
                                    </div>

                                    {/* Action Button */}
                                    <button className="w-full bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700 text-white font-medium py-2 px-4 rounded-lg transition-all duration-200 transform hover:scale-105 flex items-center justify-center gap-2">
                                        <Eye className="w-4 h-4" />
                                        View Detailed Report
                                    </button>
                                </div>
                            ))
                        ) : (
                            <div className="col-span-full text-center py-12">
                                <FileText className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                                <h3 className="text-xl font-bold text-gray-600 mb-2">No Assessment Results Found</h3>
                                <p className="text-gray-500">
                                    {filterType !== 'all' || filterSubject !== 'all' 
                                        ? 'Try adjusting your filter criteria.'
                                        : 'No assessment results have been recorded yet.'
                                    }
                                </p>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </Layout>
    );
};

export default AssessmentResults;